import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PhoneInput } from "@/components/resource/dialog-kit";
import {
  ArrowLeftRight,
  Ban,
  BedDouble,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  CircleUserRound,
  Clock3,
  LogIn,
  LogOut,
  Pencil,
  Percent,
  Plus,
  ReceiptText,
  Repeat,
  ShieldCheck,
  Trash2,
  UserPlus,
  Utensils,
  type LucideIcon,
} from "lucide-react";
import { ApiError, api } from "@/lib/api-client";
import { queryClient } from "@/lib/query-client";
import { useAppStore } from "@/store/app-store";
import type { Booking, BookingGuestMember, Payment, Room } from "@/types/domain";
import type { InventoryRoom } from "@/features/inventory/types";
import { PROPERTY_TIME_ZONE, currency, todayIso as propertyTodayIso } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import { RoomAllotmentPicker } from "@/features/bookings/RoomAllotmentPicker";
import { AddRoomForm, RoomStaysPanel, type RoomAction } from "@/features/bookings/RoomStaysPanel";
import { roomStayLabel } from "@/features/bookings/room-stay";
import { ResourceDialog, type ListResponse } from "@/components/resource/dialog-kit";
import { listItems } from "@/components/resource/dialog-utils";
import type { Service } from "@/features/settings/ServicesSettingsPage";
import type { RatePlan } from "@/features/settings/RatePlansSettingsPage";

type StayCharge = { type: "NONE" | "FULL" | "MANUAL"; charge?: number };
type StayChargeQuote = { kind: "CHECK_IN" | "CHECK_OUT"; applicable: boolean; earlyArrivalNights?: number; bookedCheckInDate?: string; scheduledAt: string; graceMinutes: number; suggestedType: StayCharge["type"]; suggestedCharge: number };
type DateChangeQuote = { available: boolean; availabilityMessage: string | null; previousNights: number; newNights: number; nightlyRate: number; previousTotal: number; newTotal: number; rooms?: { bookingRoomId: string; roomName: string; available: boolean; previousAmount: number; newAmount: number }[] };
type WithScope = { bookingRoomIds?: string[] };
type BookingAction = "add-room" | "change-dates" | "check-in" | "check-out" | "record-payment" | "record-refund" | "extend" | "change-room" | "rate-plan" | "exchange" | "confirm-room" | null;

export type InitialBookingAction = "change-dates" | "check-in" | "check-out" | "extend" | "change-room" | "exchange" | "remove-room" | "add-room" | "record-payment";

export function BookingDetailDialog({ booking, role, initialAction, initialRoomIds, onClose }: { booking: Booking; role: "ADMIN" | "OWNER"; initialAction?: InitialBookingAction; initialRoomIds?: string[]; onClose: () => void }) {
  const [current, setCurrent] = useState(booking);
  // `booking` is whatever the caller had cached (a list row can be stale - the query client doesn't
  // refetch on focus - or another session/inventory action can have moved it on since). Fetch the
  // live record on open so buttons like Check in never fire against a booking that has, say, already
  // been fully checked out elsewhere.
  const liveBooking = useQuery({
    queryKey: ["booking", booking.id],
    queryFn: () => api<Booking>(`/bookings/${booking.id}`),
    staleTime: 0,
    refetchOnMount: "always",
  });
  useEffect(() => {
    if (liveBooking.data) setCurrent(liveBooking.data);
  }, [liveBooking.data]);
  const [activeAction, setActiveAction] = useState<BookingAction>(null);
  const [addingGuest, setAddingGuest] = useState(false);
  const [editingGuest, setEditingGuest] = useState<BookingGuestMember | null>(null);
  const [addingService, setAddingService] = useState(false);
  const [addingCharge, setAddingCharge] = useState(false);
  const [chargeQuote, setChargeQuote] = useState<StayChargeQuote | null>(null);
  const [datesPassed, setDatesPassed] = useState(false);
  // Which rooms the running action applies to: undefined = every eligible room (bulk), otherwise just these.
  const [selectedRoomIds, setSelectedRoomIds] = useState<string[]>([]);
  const [actionScope, setActionScope] = useState<string[] | undefined>(undefined);
  const propertyId = useAppStore((state) => state.activeProperty!.id);
  const nights = Math.max(1, Math.round((new Date(current.checkOutDate).getTime() - new Date(current.checkInDate).getTime()) / 86400000));
  const paidAmount = (current.payments ?? []).filter((payment) => payment.status === "SUCCESS").reduce((sum, payment) => sum + Number(payment.amount), 0);
  const refundAmount = (current.payments ?? []).filter((payment) => payment.status === "REFUNDED").reduce((sum, payment) => sum + Number(payment.amount), 0);
  const due = Number(current.totalAmount) - (paidAmount - refundAmount);
  const balance = Math.max(0, due);
  const roomBasePrice = Number(current.room?.basePrice ?? 0);
  const ratePlanNightly = current.ratePlan
    ? current.ratePlan.amountType === "FLAT"
      ? roomBasePrice + Number(current.ratePlan.amountValue)
      : roomBasePrice + roomBasePrice * (Number(current.ratePlan.amountValue) / 100)
    : roomBasePrice;
  const roomSubtotal = roomBasePrice * nights;
  const ratePlanAdjustment = (ratePlanNightly - roomBasePrice) * nights;
  const servicesSubtotal = (current.services ?? []).reduce((sum, line) => sum + Number(line.price) * line.quantity, 0);
  const servicesTax = (current.services ?? []).reduce((sum, line) => sum + Number(line.taxAmount ?? 0), 0);
  const extraChargesTotal = (current.charges ?? []).reduce((sum, charge) => sum + Number(charge.amount), 0);
  const earlyCheckinCharge = Number(current.earlyCheckinCharge ?? 0);
  const lateCheckoutCharge = Number(current.lateCheckoutCharge ?? 0);
  // Bookings with allotted rooms price each room on its own stay (rate plan already inside its amount).
  const pricedRooms = (current.allottedRooms ?? []).filter((room) => room.status !== "CANCELLED");
  const roomsAmount = pricedRooms.reduce((sum, room) => sum + Number(room.amount), 0);
  const computedSubtotal = (pricedRooms.length ? roomsAmount : roomSubtotal + ratePlanAdjustment) + servicesSubtotal + servicesTax + extraChargesTotal + earlyCheckinCharge + lateCheckoutCharge;
  const otherAdjustment = Number(current.totalAmount) - computedSubtotal;
  const todayIso = propertyTodayIso();
  const stayRooms = current.allottedRooms ?? [];
  const liveRooms = stayRooms.filter((room) => room.status !== "CANCELLED");
  const multiRoom = liveRooms.length > 1;
  const anyToCheckIn = stayRooms.length ? stayRooms.some((room) => room.status === "CONFIRMED") : current.bookingStatus === "CONFIRMED";
  const anyToCheckOut = stayRooms.length ? stayRooms.some((room) => room.status === "CHECKED_IN") : current.bookingStatus === "CHECKED_IN";
  const scopeLabel = selectedRoomIds.length ? ` (${selectedRoomIds.length} selected)` : multiRoom ? " (all rooms)" : "";
  const statusOptions = getBookingStatusOptions(current.bookingStatus);
  const canOperate = current.bookingStatus === "CONFIRMED" || current.bookingStatus === "CHECKED_IN";
  const canEditExtras = ["PENDING", "CONFIRMED", "CHECKED_IN"].includes(current.bookingStatus);
  // App (GoAdapter) bookings are prepaid: the server refuses anything that re-prices their rooms
  // (dates, extend, add/remove room, rate plan, early arrival), so those actions aren't offered (N-2, N-4).
  const prepaidApp = current.source === "SAAS_ADAPTER";

  const [initialActionHandled, setInitialActionHandled] = useState(false);
  const applyUpdate = (updated: Booking) => {
    setCurrent(updated);
    setActiveAction(null);
    setSelectedRoomIds([]);
    setActionScope(undefined);
    void queryClient.invalidateQueries({ queryKey: ["bookings"] });
  };

  const statusMutation = useMutation({
    mutationFn: (input: { status: Booking["bookingStatus"]; roomIds?: string[] }) => api<Booking>(`/bookings/${current.id}/status`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const checkInMutation = useMutation({
    mutationFn: (input: StayCharge & WithScope) => api<Booking>(`/bookings/${current.id}/check-in`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const checkOutMutation = useMutation({
    mutationFn: (input: StayCharge & WithScope) => api<Booking>(`/bookings/${current.id}/check-out`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
    // The server refuses the final check-out until the balance is settled - take the user straight to the form that settles it.
    onError: (error) => {
      if (!(error instanceof ApiError) || error.status !== 422) return;
      if (/overpaid/i.test(error.message)) setActiveAction("record-refund");
      else if (/payment of .* is pending/i.test(error.message)) setActiveAction("record-payment");
    },
  });
  // A 409 here almost always means `current` was stale (booking moved on elsewhere) - refetch so the
  // buttons correct themselves instead of the user retrying against a booking state that no longer exists.
  useEffect(() => {
    if (checkInMutation.error || checkOutMutation.error) void liveBooking.refetch().then((result) => { if (result.data) setCurrent(result.data); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInMutation.error, checkOutMutation.error]);
  const changeDatesMutation = useMutation({
    mutationFn: (input: { checkInDate: string; checkOutDate: string } & WithScope) => api<Booking>(`/bookings/${current.id}/dates`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const [quoting, setQuoting] = useState(false);
  // The server decides whether an extra charge applies. Inside the allowed window we skip the charge UI
  // entirely and go straight to check-in / check-out.
  const startStayAction = async (kind: "check-in" | "check-out", ids?: string[]) => {
    setActionScope(ids);
    if (kind === "check-in") {
      const pool = stayRooms.filter((room) => room.status === "CONFIRMED" && (!ids?.length || ids.includes(room.id)));
      const passed = pool.length ? pool.some((room) => room.checkInDate.slice(0, 10) < todayIso) : current.checkInDate.slice(0, 10) < todayIso;
      if (passed) {
        setDatesPassed(true);
        setActiveAction("change-dates");
        return;
      }
    }
    setQuoting(true);
    try {
      const scope = ids?.length ? `&bookingRoomIds=${ids.join(",")}` : "";
      const quote = await api<StayChargeQuote>(`/bookings/${current.id}/stay-charge-quote?kind=${kind}${scope}`);
      if (quote.applicable) {
        setChargeQuote(quote);
        setActiveAction(kind);
      } else if (kind === "check-in") {
        checkInMutation.mutate({ type: "NONE", bookingRoomIds: ids });
      } else {
        checkOutMutation.mutate({ type: "NONE", bookingRoomIds: ids });
      }
    } catch (error) {
      setChargeQuote(null);
      setActiveAction(kind);
      console.error(error);
    } finally {
      setQuoting(false);
    }
  };
  // One entry point for every room action: `ids` = a specific room (its own card), or the ticked rooms, or all eligible.
  const runRoomAction = (action: RoomAction | "add-room", explicit?: string[]) => {
    const ids = explicit ?? (selectedRoomIds.length ? selectedRoomIds : undefined);
    setActionScope(ids);
    if (action === "check-in" || action === "check-out") return void startStayAction(action, ids);
    if (action === "remove-room") {
      const stay = stayRooms.find((room) => room.id === ids?.[0]);
      if (stay && window.confirm(`Remove ${roomStayLabel(stay)} from this booking? The total goes down by its amount.`)) removeRoomMutation.mutate(stay.id);
      return;
    }
    setDatesPassed(false);
    setActiveAction(action);
  };
  useEffect(() => {
    if (!initialAction || initialActionHandled) return;
    setInitialActionHandled(true);
    if (initialAction === "record-payment") setActiveAction("record-payment");
    else runRoomAction(initialAction, initialRoomIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAction, initialActionHandled]);
  const addRoomMutation = useMutation({
    mutationFn: (roomId: string) => api<Booking>(`/bookings/${current.id}/rooms`, { method: "POST", body: { roomId } }),
    onSuccess: applyUpdate,
  });
  const removeRoomMutation = useMutation({
    mutationFn: (bookingRoomId: string) => api<Booking>(`/bookings/${current.id}/rooms/${bookingRoomId}`, { method: "DELETE" }),
    onSuccess: applyUpdate,
  });
  const recordPaymentMutation = useMutation({
    mutationFn: async (input: { amount: number; paymentMode: "CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER"; transactionRef?: string }) => {
      await api<Payment>(`/payments`, { method: "POST", body: { bookingId: current.id, ...input } });
      return api<Booking>(`/bookings/${current.id}`);
    },
    onSuccess: applyUpdate,
  });
  const recordRefundMutation = useMutation({
    mutationFn: async (input: { amount: number; paymentMode: "CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER"; transactionRef?: string }) => {
      await api<Payment>(`/payments`, { method: "POST", body: { bookingId: current.id, status: "REFUNDED", ...input } });
      return api<Booking>(`/bookings/${current.id}`);
    },
    onSuccess: applyUpdate,
  });
  const extendMutation = useMutation({
    mutationFn: (input: { checkOutDate: string } & WithScope) => api<Booking>(`/bookings/${current.id}/extend`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const roomChangeMutation = useMutation({
    mutationFn: (input: { roomId: string; bookingRoomId?: string }) => api<Booking>(`/bookings/${current.id}/room`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const ratePlanMutation = useMutation({
    mutationFn: (input: { ratePlanId: string | null }) => api<Booking>(`/bookings/${current.id}/rate-plan`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const exchangeMutation = useMutation({
    mutationFn: (input: { withBookingId: string; withBookingRoomId?: string; bookingRoomId?: string }) => api<Booking>(`/bookings/${current.id}/exchange`, { method: "POST", body: input }),
    onSuccess: applyUpdate,
  });
  const addGuestMutation = useMutation({
    mutationFn: (input: { name: string; phone?: string; email?: string }) => api<Booking>(`/bookings/${current.id}/guests`, { method: "POST", body: input }),
    onSuccess: (updated) => { setCurrent(updated); setAddingGuest(false); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const updateGuestMutation = useMutation({
    mutationFn: (input: { guestId: string; name: string; phone?: string; email?: string }) => api<Booking>(`/bookings/${current.id}/guests/${input.guestId}`, { method: "PATCH", body: input }),
    onSuccess: (updated) => { setCurrent(updated); setEditingGuest(null); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const removeGuestMutation = useMutation({
    mutationFn: (guestId: string) => api<Booking>(`/bookings/${current.id}/guests/${guestId}`, { method: "DELETE" }),
    onSuccess: (updated) => { setCurrent(updated); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const addServiceMutation = useMutation({
    mutationFn: (input: { serviceId: string; quantity: number }) => api<Booking>(`/bookings/${current.id}/services`, { method: "POST", body: input }),
    onSuccess: (updated) => { setCurrent(updated); setAddingService(false); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const updateServiceQtyMutation = useMutation({
    mutationFn: (input: { lineId: string; quantity: number }) => api<Booking>(`/bookings/${current.id}/services/${input.lineId}`, { method: "PATCH", body: { quantity: input.quantity } }),
    onSuccess: (updated) => { setCurrent(updated); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const removeServiceMutation = useMutation({
    mutationFn: (lineId: string) => api<Booking>(`/bookings/${current.id}/services/${lineId}`, { method: "DELETE" }),
    onSuccess: (updated) => { setCurrent(updated); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const addChargeMutation = useMutation({
    mutationFn: (input: { label: string; amount: number }) => api<Booking>(`/bookings/${current.id}/charges`, { method: "POST", body: input }),
    onSuccess: (updated) => { setCurrent(updated); setAddingCharge(false); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const removeChargeMutation = useMutation({
    mutationFn: (chargeId: string) => api<Booking>(`/bookings/${current.id}/charges/${chargeId}`, { method: "DELETE" }),
    onSuccess: (updated) => { setCurrent(updated); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });

  return (
    <ResourceDialog title={current.bookingRef} eyebrow={`${role === "ADMIN" ? "Admin" : "Owner"} booking control`} dialogClassName="booking-dialog" onClose={onClose}>
      <div className="booking-detail">
        <section className="booking-detail__hero">
          <div>
            <span className={`status status--${current.bookingStatus.toLowerCase()}`}>{current.bookingStatus}</span>
            <h3>{current.guestName}</h3>
            <p>{(current.roomsCount ?? 1) > 1 && current.allottedRooms?.length ? current.allottedRooms.map((entry) => entry.room.name).join(", ") : current.room?.name ?? (current.roomType ? `${current.roomType.name} · room${(current.roomsCount ?? 1) > 1 ? "s" : ""} not yet allotted` : "Room")}{(current.roomsCount ?? 1) > 1 ? ` (${current.roomsCount} rooms)` : ""}</p>
          </div>
          <strong>{currency.format(Number(current.totalAmount))}</strong>
        </section>

        <section className="booking-highlight-grid">
          <BookingHighlight icon={Clock3} label="Check-in" value={formatDateTime(current.checkInDate, current.stayProfile?.checkInTime)} tone="orange" />
          <BookingHighlight icon={Clock3} label="Check-out" value={formatDateTime(current.checkOutDate, current.stayProfile?.checkOutTime)} tone="green" />
          <BookingHighlight icon={CircleUserRound} label="Guests" value={`${current.noOfGuests} guest${current.noOfGuests === 1 ? "" : "s"} - ${nights} night${nights === 1 ? "" : "s"}`} tone="blue" />
          <BookingHighlight icon={CircleDollarSign} label="Payment" value={`${current.paymentStatus ?? "UNPAID"} - ${currency.format(Math.abs(due))} ${due < 0 ? "refund due" : "due"}`} tone="violet" />
        </section>

        <section className="booking-detail-grid">
          <BookingInfoPanel title="Guest" icon={CircleUserRound} rows={[
            ["Name", current.guestName],
            ["Phone", current.guestPhone],
            ["Email", current.guestEmail || "Not provided"],
          ]} />
          <BookingInfoPanel title="Room" icon={BedDouble} rows={[
            ["Room type", current.roomType?.name ?? "Not provided"],
            ["Room", (current.roomsCount ?? 1) > 1 && current.allottedRooms?.length ? current.allottedRooms.map((entry) => entry.room.name).join(", ") : current.room?.name ?? "Not yet allotted"],
            ["Number", current.room?.roomNumber || "Unassigned"],
            ["Base price", current.room?.basePrice === undefined ? "Not provided" : currency.format(Number(current.room.basePrice))],
          ]} />
          <BookingInfoPanel title="Booking" icon={ReceiptText} rows={[
            ["Source", (current.source ?? "DIRECT_SITE").replaceAll("_", " ")],
            ["Rate plan", current.ratePlan ? `${current.ratePlan.name} (${current.ratePlan.amountType === "PERCENTAGE" ? `${current.ratePlan.amountValue}%` : currency.format(Number(current.ratePlan.amountValue))})` : "None"],
            ["Created", current.createdAt ? formatDateTime(current.createdAt) : "Not provided"],
            ["Responded", current.respondedAt ? formatDateTime(current.respondedAt) : "Awaiting response"],
          ]} />
          <BookingInfoPanel title="Stay" icon={LogIn} rows={[
            ["Checked in", current.checkedInAt ? formatDateTime(current.checkedInAt) : "Not yet"],
            ["Checked out", current.checkedOutAt ? formatDateTime(current.checkedOutAt) : "Not yet"],
            ...(current.earlyCheckinType && current.earlyCheckinType !== "NONE" ? [["Early check-in", `${current.earlyCheckinType === "FULL" ? "Full night" : "Manual"} · ${currency.format(Number(current.earlyCheckinCharge ?? 0))}`] as [string, string]] : []),
            ...(current.lateCheckoutType && current.lateCheckoutType !== "NONE" ? [["Late checkout", `${current.lateCheckoutType === "FULL" ? "Full night" : "Manual"} · ${currency.format(Number(current.lateCheckoutCharge ?? 0))}`] as [string, string]] : []),
          ]} />
        </section>

        <section className="booking-line-section">
          <div className="booking-line-section__head">
            <h4><UserPlus size={16} /> Guests</h4>
            {canEditExtras && <button type="button" className="booking-line-section__add" onClick={() => { setAddingGuest(true); setEditingGuest(null); }}><Plus size={13} /> Add</button>}
          </div>
          {current.guests?.length ? (
            <div className="booking-line-list">
              {current.guests.map((guest) => (
                <div className="booking-line-item" key={guest.id}>
                  <div><strong>{guest.name}</strong><small>{guest.phone || guest.email || "No contact info"}</small></div>
                  {canEditExtras && (
                    <span className="booking-line-item__actions">
                      <button type="button" aria-label={`Edit ${guest.name}`} onClick={() => { setEditingGuest(guest); setAddingGuest(false); }}><Pencil size={14} /></button>
                      <button type="button" aria-label={`Remove ${guest.name}`} disabled={removeGuestMutation.isPending} onClick={() => removeGuestMutation.mutate(guest.id)}><Trash2 size={14} /></button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : <p className="booking-line-section__empty">No additional guests added.</p>}
          {addingGuest && (
            <GuestForm loading={addGuestMutation.isPending} error={addGuestMutation.error} onCancel={() => setAddingGuest(false)} onSubmit={(input) => addGuestMutation.mutate(input)} />
          )}
          {editingGuest && (
            <GuestForm initial={editingGuest} loading={updateGuestMutation.isPending} error={updateGuestMutation.error} onCancel={() => setEditingGuest(null)} onSubmit={(input) => updateGuestMutation.mutate({ guestId: editingGuest.id, ...input })} />
          )}
        </section>

        <section className="booking-line-section">
          <div className="booking-line-section__head">
            <h4><Utensils size={16} /> Services</h4>
            {canEditExtras && <button type="button" className="booking-line-section__add" onClick={() => setAddingService(true)}><Plus size={13} /> Add</button>}
          </div>
          {current.services?.length ? (
            <div className="booking-line-list">
              {current.services.map((line) => (
                <div className="booking-line-item" key={line.id}>
                  <div>
                    <span className="booking-line-item__title-row">
                      <strong>{line.title}</strong>
                      <span className={`service-tag ${line.includedInRatePlan ? "service-tag--rate-plan" : "service-tag--added"}`}>{line.includedInRatePlan ? "Rate plan" : "Added"}</span>
                    </span>
                    <small>{currency.format(Number(line.price))} × {line.quantity}{Number(line.taxAmount ?? 0) > 0 ? ` + GST ${Number(line.taxPercent ?? 0)}% (${currency.format(Number(line.taxAmount))})` : ""}</small>
                  </div>
                  <strong className="booking-line-item__value">{currency.format(Number(line.price) * line.quantity + Number(line.taxAmount ?? 0))}</strong>
                  {canEditExtras && (
                    <span className="booking-line-item__actions">
                      <button type="button" aria-label={`Increase ${line.title} quantity`} disabled={updateServiceQtyMutation.isPending} onClick={() => updateServiceQtyMutation.mutate({ lineId: line.id, quantity: line.quantity + 1 })}>+</button>
                      <button type="button" aria-label={`Decrease ${line.title} quantity`} disabled={updateServiceQtyMutation.isPending || line.quantity <= 1} onClick={() => updateServiceQtyMutation.mutate({ lineId: line.id, quantity: line.quantity - 1 })}>-</button>
                      <button type="button" aria-label={`Remove ${line.title}`} disabled={removeServiceMutation.isPending} onClick={() => removeServiceMutation.mutate(line.id)}><Trash2 size={14} /></button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : <p className="booking-line-section__empty">No services added to this booking.</p>}
          {addingService && (
            <AddServiceForm propertyId={propertyId} loading={addServiceMutation.isPending} error={addServiceMutation.error} onCancel={() => setAddingService(false)} onSubmit={(input) => addServiceMutation.mutate(input)} />
          )}
        </section>

        <section className="booking-line-section">
          <div className="booking-line-section__head">
            <h4><ReceiptText size={16} /> Extra charges</h4>
            {canEditExtras && <button type="button" className="booking-line-section__add" onClick={() => setAddingCharge(true)}><Plus size={13} /> Add</button>}
          </div>
          {current.charges?.length ? (
            <div className="booking-line-list">
              {current.charges.map((charge) => (
                <div className="booking-line-item" key={charge.id}>
                  <div><strong>{charge.label}</strong></div>
                  <strong className="booking-line-item__value">{currency.format(Number(charge.amount))}</strong>
                  {canEditExtras && (
                    <span className="booking-line-item__actions">
                      <button type="button" aria-label={`Remove ${charge.label}`} disabled={removeChargeMutation.isPending} onClick={() => removeChargeMutation.mutate(charge.id)}><Trash2 size={14} /></button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : <p className="booking-line-section__empty">No extra charges added.</p>}
          {addingCharge && (
            <AddChargeForm loading={addChargeMutation.isPending} error={addChargeMutation.error} onCancel={() => setAddingCharge(false)} onSubmit={(input) => addChargeMutation.mutate(input)} />
          )}
        </section>

        <section className="booking-pricing-chart">
          <h4><ReceiptText size={16} /> Pricing breakdown</h4>
          <div className="booking-pricing-chart__rows">
            {pricedRooms.length ? pricedRooms.map((room) => (
              <div key={room.id}><span>{roomStayLabel(room)} ({Math.max(1, Math.round((new Date(room.checkOutDate).getTime() - new Date(room.checkInDate).getTime()) / 86_400_000))} night(s){current.ratePlan ? ` · ${current.ratePlan.name}` : ""})</span><strong>{currency.format(Number(room.amount))}</strong></div>
            )) : <div><span>Room ({nights} night{nights === 1 ? "" : "s"} × {currency.format(roomBasePrice)})</span><strong>{currency.format(roomSubtotal)}</strong></div>}
            {!pricedRooms.length && current.ratePlan && (
              <div><span>Rate plan — {current.ratePlan.name} ({current.ratePlan.amountType === "PERCENTAGE" ? `${current.ratePlan.amountValue}%` : "flat"})</span><strong>{ratePlanAdjustment >= 0 ? "+" : ""}{currency.format(ratePlanAdjustment)}</strong></div>
            )}
            {servicesSubtotal > 0 && <div><span>Services</span><strong>{currency.format(servicesSubtotal)}</strong></div>}
            {servicesTax > 0 && <div><span>GST on services</span><strong>{currency.format(servicesTax)}</strong></div>}
            {earlyCheckinCharge > 0 && <div><span>Early check-in charge</span><strong>{currency.format(earlyCheckinCharge)}</strong></div>}
            {lateCheckoutCharge > 0 && <div><span>Late checkout charge</span><strong>{currency.format(lateCheckoutCharge)}</strong></div>}
            {extraChargesTotal > 0 && <div><span>Extra charges</span><strong>{currency.format(extraChargesTotal)}</strong></div>}
            {Math.abs(otherAdjustment) > 0.5 && <div><span>Other adjustment</span><strong>{otherAdjustment >= 0 ? "+" : ""}{currency.format(otherAdjustment)}</strong></div>}
            <div className="booking-pricing-chart__total"><span>Total</span><strong>{currency.format(Number(current.totalAmount))}</strong></div>
          </div>
        </section>

        <section className="booking-payment-card">
          <div className="booking-payment-card__head">
            <span className={`status status--${(current.paymentStatus ?? "UNPAID").toLowerCase()}`}>{current.paymentStatus ?? "UNPAID"}</span>
          </div>
          <div className="booking-payment-card__grid">
            <div><small>Total</small><strong>{currency.format(Number(current.totalAmount))}</strong></div>
            <div><small>Paid</small><strong>{currency.format(paidAmount)}</strong></div>
            <div><small>Refund</small><strong>{currency.format(refundAmount)}</strong></div>
            <div className={due > 0 ? "booking-payment-card__due--pending" : due < 0 ? "booking-payment-card__due--refund" : "booking-payment-card__due--settled"}>
              <small>{due < 0 ? "Refund due" : "Due"}</small><strong>{currency.format(Math.abs(due))}</strong>
            </div>
          </div>
        </section>


        {stayRooms.length > 1 && (
          <RoomStaysPanel
            booking={current}
            canOperate={canOperate}
            prepaidApp={prepaidApp}
            selected={selectedRoomIds}
            busy={quoting || checkInMutation.isPending || checkOutMutation.isPending || removeRoomMutation.isPending}
            onSelect={setSelectedRoomIds}
            onAction={runRoomAction}
          />
        )}
        {removeRoomMutation.error && <div className="form-error">{removeRoomMutation.error.message}</div>}

        <section className="booking-actions">
          <div>
            <ShieldCheck size={18} />
            <span>{role === "ADMIN" ? "Admin can supervise and update this booking." : "Owner can control bookings for assigned properties."}</span>
          </div>
          <div>
            {anyToCheckIn && <button type="button" className="booking-action booking-action--primary" disabled={quoting || checkInMutation.isPending} onClick={() => runRoomAction("check-in")}><LogIn size={15} /> Check in{scopeLabel}</button>}
            {anyToCheckOut && <button type="button" className="booking-action booking-action--primary" disabled={quoting || checkOutMutation.isPending} onClick={() => runRoomAction("check-out")}><LogOut size={15} /> Check out{scopeLabel}</button>}
            {canEditExtras && due > 0 && <button type="button" className="booking-action booking-action--primary" onClick={() => setActiveAction("record-payment")}><CircleDollarSign size={15} /> Record payment ({currency.format(due)})</button>}
            {canEditExtras && due < 0 && <button type="button" className="booking-action booking-action--neutral" onClick={() => setActiveAction("record-refund")}><CircleDollarSign size={15} /> Record refund ({currency.format(Math.abs(due))})</button>}
            {canOperate && !prepaidApp && <button type="button" className="booking-action booking-action--neutral" onClick={() => runRoomAction("change-dates")}><CalendarDays size={15} /> Change stay date{scopeLabel}</button>}
            {canOperate && !prepaidApp && <button type="button" className="booking-action booking-action--neutral" onClick={() => runRoomAction("extend")}><Clock3 size={15} /> Extend stay{scopeLabel}</button>}
            {canOperate && !multiRoom && <button type="button" className="booking-action booking-action--neutral" onClick={() => runRoomAction("change-room", liveRooms[0] ? [liveRooms[0].id] : undefined)}><Repeat size={15} /> Change room</button>}
            {canOperate && !multiRoom && <button type="button" className="booking-action booking-action--neutral" onClick={() => runRoomAction("exchange", liveRooms[0] ? [liveRooms[0].id] : undefined)}><ArrowLeftRight size={15} /> Exchange room</button>}
            {canOperate && !prepaidApp && <button type="button" className="booking-action booking-action--neutral" onClick={() => runRoomAction("add-room")}><Plus size={15} /> Add room</button>}
            {canEditExtras && !prepaidApp && <button type="button" className="booking-action booking-action--neutral" onClick={() => setActiveAction("rate-plan")}><Percent size={15} /> Rate plan</button>}
            {statusOptions.map((status) => <button type="button" key={status} className={`booking-action booking-action--${status.toLowerCase()}`} disabled={statusMutation.isPending} onClick={() => (status === "CONFIRMED" ? setActiveAction("confirm-room") : statusMutation.mutate({ status }))}>{statusIcon(status)} {statusLabel(status)}</button>)}
          </div>
        </section>
        {statusMutation.error && <div className="form-error">{statusMutation.error.message}</div>}
        {prepaidApp && canEditExtras && <div className="form-warning">Prepaid app booking: its dates, rooms and rate plan can't be changed here. Services and extra charges can still be added and collected at the hotel.</div>}
        {canEditExtras && due > 0 && !activeAction && <div className="form-warning">Payment due: {currency.format(due)}. Tariff is recalculated automatically after every change - use Record payment to collect it.</div>}
        {(checkInMutation.error || checkOutMutation.error) && !activeAction && <div className="form-error">{(checkInMutation.error ?? checkOutMutation.error)?.message}</div>}
        {activeAction === "change-dates" && (
          <ChangeStayDatesForm
            booking={current}
            notice={datesPassed ? "This booking's check-in date has passed, so direct check-in isn't allowed. Pick new stay dates to continue." : undefined}
            loading={changeDatesMutation.isPending}
            error={changeDatesMutation.error}
            onCancel={() => setActiveAction(null)}
            scope={actionScope}
            onSubmit={(input) => changeDatesMutation.mutate({ ...input, bookingRoomIds: actionScope })}
          />
        )}

        {activeAction === "check-in" && (
          <StayChargeForm
            title="Check in guest"
            chargeLabel="Early check-in charge"
            nightlyRate={chargeQuote?.suggestedCharge ?? Number(current.room?.basePrice ?? 0)}
            initialType={chargeQuote?.suggestedType}
            loading={checkInMutation.isPending}
            error={checkInMutation.error}
            blocked={prepaidApp && Boolean(chargeQuote?.earlyArrivalNights)}
            banner={prepaidApp && chargeQuote?.earlyArrivalNights ? `This is a prepaid app booking for ${chargeQuote.bookedCheckInDate} - it can only be checked in from that date. For an earlier arrival, create a separate booking for the extra night(s).` : chargeQuote?.earlyArrivalNights ? `Guest is arriving ${chargeQuote.earlyArrivalNights} day(s) before the booked check-in (${chargeQuote.bookedCheckInDate}). The stay will start today and ${chargeQuote.earlyArrivalNights} extra night(s) will be added to the total.${chargeQuote.suggestedCharge ? " An early check-in charge also applies." : ""}` : chargeQuote?.applicable ? `Early check-in — scheduled check-in is ${formatDateTime(current.checkInDate, current.stayProfile?.checkInTime)}. Guest is arriving before that, so an extra charge applies.` : undefined}
            onCancel={() => setActiveAction(null)}
            onSubmit={(input) => checkInMutation.mutate({ ...input, bookingRoomIds: actionScope })}
          />
        )}
        {activeAction === "record-payment" && (
          <RecordPaymentForm
            balance={balance}
            loading={recordPaymentMutation.isPending}
            error={recordPaymentMutation.error}
            onCancel={() => setActiveAction(null)}
            onSubmit={(input) => recordPaymentMutation.mutate(input)}
          />
        )}
        {activeAction === "record-refund" && (
          <RecordRefundForm
            overpaid={Math.abs(Math.min(0, due))}
            loading={recordRefundMutation.isPending}
            error={recordRefundMutation.error}
            onCancel={() => setActiveAction(null)}
            onSubmit={(input) => recordRefundMutation.mutate(input)}
          />
        )}
        {activeAction === "check-out" && (
          <StayChargeForm
            title="Check out guest"
            chargeLabel="Late check-out charge"
            nightlyRate={chargeQuote?.suggestedCharge ?? Number(current.room?.basePrice ?? 0)}
            initialType={chargeQuote?.suggestedType}
            loading={checkOutMutation.isPending}
            error={checkOutMutation.error}
            banner={chargeQuote?.applicable ? `Late check-out — scheduled check-out is ${formatDateTime(current.checkOutDate, current.stayProfile?.checkOutTime)}. Guest is leaving after the grace period, so an extra charge applies.` : undefined}
            onCancel={() => setActiveAction(null)}
            onSubmit={(input) => checkOutMutation.mutate({ ...input, bookingRoomIds: actionScope })}
          />
        )}
        {activeAction === "extend" && (
          <ExtendStayForm
            booking={current}
            loading={extendMutation.isPending}
            error={extendMutation.error}
            onCancel={() => setActiveAction(null)}
            scope={actionScope}
            onSubmit={(checkOutDate) => extendMutation.mutate({ checkOutDate, bookingRoomIds: actionScope })}
          />
        )}
        {activeAction === "confirm-room" && (
          <ConfirmRoomForm
            booking={current}
            propertyId={propertyId}
            loading={statusMutation.isPending}
            error={statusMutation.error}
            onCancel={() => setActiveAction(null)}
            onSubmit={(roomIds) => statusMutation.mutate({ status: "CONFIRMED", roomIds })}
          />
        )}
        {activeAction === "change-room" && (
          <ChangeRoomForm
            booking={current}
            propertyId={propertyId}
            loading={roomChangeMutation.isPending}
            error={roomChangeMutation.error}
            onCancel={() => setActiveAction(null)}
            scopeRoomId={actionScope?.[0]}
            onSubmit={(roomId) => roomChangeMutation.mutate({ roomId, bookingRoomId: actionScope?.[0] })}
          />
        )}
        {activeAction === "exchange" && (
          <ExchangeRoomForm
            booking={current}
            propertyId={propertyId}
            loading={exchangeMutation.isPending}
            error={exchangeMutation.error}
            onCancel={() => setActiveAction(null)}
            scopeRoomId={actionScope?.[0]}
            onSubmit={(withBookingId, withBookingRoomId) => exchangeMutation.mutate({ withBookingId, withBookingRoomId, bookingRoomId: actionScope?.[0] })}
          />
        )}
        {activeAction === "add-room" && (
          <AddRoomForm
            booking={current}
            propertyId={propertyId}
            loading={addRoomMutation.isPending}
            error={addRoomMutation.error}
            onCancel={() => setActiveAction(null)}
            onSubmit={(roomId) => addRoomMutation.mutate(roomId)}
          />
        )}
        {activeAction === "rate-plan" && (
          <RatePlanForm
            booking={current}
            propertyId={propertyId}
            loading={ratePlanMutation.isPending}
            error={ratePlanMutation.error}
            onCancel={() => setActiveAction(null)}
            onSubmit={(input) => ratePlanMutation.mutate(input)}
          />
        )}
      </div>
    </ResourceDialog>
  );
}

function StayChargeForm({ title, chargeLabel, nightlyRate, initialType, loading, error, banner, blocked = false, onCancel, onSubmit }: {
  title: string;
  chargeLabel: string;
  nightlyRate: number;
  initialType?: StayCharge["type"];
  loading: boolean;
  error: Error | null;
  banner?: string;
  blocked?: boolean;
  onCancel: () => void;
  onSubmit: (input: StayCharge) => void;
}) {
  const [type, setType] = useState<StayCharge["type"]>(initialType ?? "NONE");
  const [amount, setAmount] = useState("");

  return (
    <section className="booking-inline-form">
      <h4>{title}</h4>
      {banner && <div className={blocked ? "form-error" : "form-warning"}>{banner}</div>}
      {!blocked && <div className="stay-charge-options">
        <label><input type="radio" name="stayChargeType" checked={type === "NONE"} onChange={() => setType("NONE")} /> No charge</label>
        <label><input type="radio" name="stayChargeType" checked={type === "FULL"} onChange={() => setType("FULL")} /> Full night ({currency.format(nightlyRate)})</label>
        <label><input type="radio" name="stayChargeType" checked={type === "MANUAL"} onChange={() => setType("MANUAL")} /> Manual amount</label>
      </div>}
      {!blocked && type === "MANUAL" && (
        <div className="property-form-grid">
          <label>{chargeLabel}<input type="number" min={0} step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
        </div>
      )}
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={blocked} onClick={() => onSubmit({ type, charge: type === "MANUAL" ? Number(amount || 0) : undefined })}>Confirm</Button>
      </div>
    </section>
  );
}

function RecordPaymentForm({ balance, loading, error, onCancel, onSubmit }: {
  balance: number;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { amount: number; paymentMode: "CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER"; transactionRef?: string }) => void;
}) {
  const [amount, setAmount] = useState(String(balance));
  const [paymentMode, setPaymentMode] = useState<"CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER">("CASH");
  const [transactionRef, setTransactionRef] = useState("");

  return (
    <section className="booking-inline-form">
      <h4>Payment pending</h4>
      <div className="form-warning">A balance of {currency.format(balance)} is due on this booking.</div>
      <div className="property-form-grid">
        <label>Amount<input type="number" min={0.01} step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
        <label>Payment mode
          <select value={paymentMode} onChange={(event) => setPaymentMode(event.target.value as typeof paymentMode)}>
            <option value="CASH">Cash</option>
            <option value="UPI">UPI</option>
            <option value="CARD">Card</option>
            <option value="BANK_TRANSFER">Bank transfer</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
        <label className="property-form-grid__wide">Transaction reference<input value={transactionRef} onChange={(event) => setTransactionRef(event.target.value)} placeholder="Optional" /></label>
      </div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!amount || Number(amount) <= 0} onClick={() => onSubmit({ amount: Number(amount), paymentMode, transactionRef: transactionRef.trim() || undefined })}>Record payment</Button>
      </div>
    </section>
  );
}

function RecordRefundForm({ overpaid, loading, error, onCancel, onSubmit }: {
  overpaid: number;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { amount: number; paymentMode: "CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER"; transactionRef?: string }) => void;
}) {
  const [amount, setAmount] = useState(String(overpaid));
  const [paymentMode, setPaymentMode] = useState<"CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER">("CASH");
  const [transactionRef, setTransactionRef] = useState("");

  return (
    <section className="booking-inline-form">
      <h4>Refund due</h4>
      <div className="form-warning">Guest has been overpaid by {currency.format(overpaid)}. Record a refund before checking out.</div>
      <div className="property-form-grid">
        <label>Amount<input type="number" min={0.01} max={overpaid} step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
        <label>Refund mode
          <select value={paymentMode} onChange={(event) => setPaymentMode(event.target.value as typeof paymentMode)}>
            <option value="CASH">Cash</option>
            <option value="UPI">UPI</option>
            <option value="CARD">Card</option>
            <option value="BANK_TRANSFER">Bank transfer</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
        <label className="property-form-grid__wide">Transaction reference<input value={transactionRef} onChange={(event) => setTransactionRef(event.target.value)} placeholder="Optional" /></label>
      </div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!amount || Number(amount) <= 0 || Number(amount) > overpaid} onClick={() => onSubmit({ amount: Number(amount), paymentMode, transactionRef: transactionRef.trim() || undefined })}>Record refund</Button>
      </div>
    </section>
  );
}

function ChangeStayDatesForm({ booking, scope, notice, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  scope?: string[];
  notice?: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { checkInDate: string; checkOutDate: string }) => void;
}) {
  const today = propertyTodayIso();
  // The rooms this change applies to (a chosen few, or every room that can still move).
  const scoped = (booking.allottedRooms ?? []).filter((room) => (room.status === "CONFIRMED" || room.status === "CHECKED_IN") && (!scope?.length || scope.includes(room.id)));
  const startIn = (scoped[0]?.checkInDate ?? booking.checkInDate).slice(0, 10);
  const startOut = (scoped[0]?.checkOutDate ?? booking.checkOutDate).slice(0, 10);
  const [checkInDate, setCheckInDate] = useState(startIn);
  const [checkOutDate, setCheckOutDate] = useState(startOut);
  const lockCheckIn = scoped.length ? scoped.some((room) => room.status === "CHECKED_IN") : booking.bookingStatus === "CHECKED_IN";
  const changed = checkInDate !== startIn || checkOutDate !== startOut;
  const validRange = Boolean(checkInDate && checkOutDate && checkOutDate > checkInDate);
  // Availability and repricing are decided by the server for exactly these dates and these rooms, every time they change.
  const quote = useQuery({
    queryKey: ["booking-date-quote", booking.id, scope?.join(",") ?? "all", checkInDate, checkOutDate],
    queryFn: () => api<DateChangeQuote>(`/bookings/${booking.id}/dates/quote`, { method: "POST", body: { checkInDate, checkOutDate, bookingRoomIds: scope } }),
    enabled: changed && validRange,
    retry: false,
    staleTime: 0,
  });
  const canSave = changed && validRange && quote.data?.available === true;
  const target = scoped.length > 1 ? `${scoped.length} rooms` : scoped[0] ? roomStayLabel(scoped[0]) : "this booking";

  return (
    <section className="booking-inline-form">
      <h4>Change stay date - {target}</h4>
      {notice && <div className="form-warning">{notice}</div>}
      <div className="property-form-grid">
        <label>Check-in date<DateInput min={today} disabled={lockCheckIn} value={checkInDate} onChange={(event) => setCheckInDate(event.target.value)} /></label>
        <label>Check-out date<DateInput min={checkInDate || today} value={checkOutDate} onChange={(event) => setCheckOutDate(event.target.value)} /></label>
      </div>
      {changed && !validRange && <div className="form-error">Check-out date must be after the check-in date.</div>}
      {changed && validRange && quote.isFetching && <div className="form-hint">Checking room availability...</div>}
      {quote.data && !quote.isFetching && !quote.data.available && (
        <div className="form-error">
          {quote.data.availabilityMessage ?? "Selected room is not available for the selected stay dates. Please select different dates or another room."}
          {quote.data.rooms && quote.data.rooms.length > 1 && <> Not free: {quote.data.rooms.filter((room) => !room.available).map((room) => room.roomName).join(", ")}.</>}
        </div>
      )}
      {quote.data?.available && !quote.isFetching && (
        <div className="form-warning">
          Room{scoped.length > 1 ? "s are" : " is"} available. {quote.data.previousNights} night(s) → {quote.data.newNights} night(s). New total: {currency.format(quote.data.previousTotal)} → {currency.format(quote.data.newTotal)}.
        </div>
      )}
      {quote.error && <div className="form-error">{quote.error.message}</div>}
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!canSave} onClick={() => onSubmit({ checkInDate, checkOutDate })}>Save new dates</Button>
      </div>
    </section>
  );
}

function ExtendStayForm({ booking, scope, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  scope?: string[];
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (checkOutDate: string) => void;
}) {
  const scoped = (booking.allottedRooms ?? []).filter((room) => (room.status === "CONFIRMED" || room.status === "CHECKED_IN") && (!scope?.length || scope.includes(room.id)));
  const latestOut = scoped.length ? new Date(Math.max(...scoped.map((room) => new Date(room.checkOutDate).getTime()))) : new Date(booking.checkOutDate);
  const minCheckOutDate = new Date(latestOut.getTime() + 86_400_000).toISOString().slice(0, 10);
  const [checkOutDate, setCheckOutDate] = useState(minCheckOutDate);
  const quote = useQuery({
    queryKey: ["booking-date-quote", booking.id, "extend", scope?.join(",") ?? "all", checkOutDate],
    queryFn: () => api<DateChangeQuote>(`/bookings/${booking.id}/dates/quote`, { method: "POST", body: { checkOutDate, bookingRoomIds: scope, mode: "EXTEND" } }),
    enabled: checkOutDate >= minCheckOutDate,
    retry: false,
    staleTime: 0,
  });
  const canSave = checkOutDate >= minCheckOutDate && quote.data?.available === true;
  const target = scoped.length > 1 ? `${scoped.length} rooms` : scoped[0] ? roomStayLabel(scoped[0]) : "this booking";

  return (
    <section className="booking-inline-form">
      <h4>Extend stay - {target}</h4>
      <div className="property-form-grid">
        <label>New check-out date<DateInput min={minCheckOutDate} value={checkOutDate} onChange={(event) => setCheckOutDate(event.target.value)} /></label>
      </div>
      {quote.isFetching && <div className="form-hint">Checking room availability...</div>}
      {quote.data && !quote.isFetching && !quote.data.available && <div className="form-error">{quote.data.availabilityMessage}</div>}
      {quote.data?.available && !quote.isFetching && (
        <div className="form-warning">Available. Total is recalculated automatically: {currency.format(quote.data.previousTotal)} → {currency.format(quote.data.newTotal)}.</div>
      )}
      {quote.error && <div className="form-error">{quote.error.message}</div>}
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!canSave} onClick={() => onSubmit(checkOutDate)}>Extend stay</Button>
      </div>
    </section>
  );
}

function ConfirmRoomForm({ booking, propertyId, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  propertyId: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (roomIds: string[]) => void;
}) {
  const rooms = useQuery({
    queryKey: ["inventory-rooms", propertyId, booking.checkInDate, booking.checkOutDate],
    queryFn: () => api<InventoryRoom[]>(`/inventory/rooms?propertyId=${propertyId}&date=${booking.checkInDate.slice(0, 10)}&checkOutDate=${booking.checkOutDate.slice(0, 10)}`),
  });
  const roomsNeeded = booking.roomsCount ?? 1;
  const [roomIds, setRoomIds] = useState<string[]>([]);
  const options = (rooms.data ?? []).filter((room) => room.status === "AVAILABLE" && room.isBookable && (!booking.roomTypeId || room.roomTypeId === booking.roomTypeId));

  return (
    <section className="booking-inline-form">
      <h4>{roomsNeeded > 1 ? `Allot ${roomsNeeded} rooms to confirm` : "Allot a room to confirm"}</h4>
      <p className="booking-inline-form__hint">Requested room type: <strong>{booking.roomType?.name ?? "Not specified"}</strong>{roomsNeeded > 1 && <> - <strong>{roomsNeeded} rooms</strong></>}</p>
      <RoomAllotmentPicker roomsNeeded={roomsNeeded} options={options} selected={roomIds} loading={rooms.isLoading} onChange={setRoomIds} />
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={roomIds.length !== roomsNeeded} onClick={() => onSubmit(roomIds)}>Confirm booking</Button>
      </div>
    </section>
  );
}

function ChangeRoomForm({ booking, propertyId, scopeRoomId, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  propertyId: string;
  scopeRoomId?: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (roomId: string) => void;
}) {
  const rooms = useQuery({ queryKey: ["rooms", propertyId], queryFn: () => api<Room[]>(`/rooms?propertyId=${propertyId}`) });
  const [roomId, setRoomId] = useState("");
  const onBooking = new Set((booking.allottedRooms ?? []).filter((room) => room.status !== "CANCELLED").map((room) => room.roomId));
  const moving = (booking.allottedRooms ?? []).find((room) => room.id === scopeRoomId);
  const options = (rooms.data ?? []).filter((room) => room.id !== booking.room?.id && !onBooking.has(room.id));

  return (
    <section className="booking-inline-form">
      <h4>Change room{moving ? ` - ${roomStayLabel(moving)}` : ""}</h4>
      <div className="property-form-grid">
        <label>Move to
          <select value={roomId} onChange={(event) => setRoomId(event.target.value)}>
            <option value="">Select a room</option>
            {options.map((room) => <option key={room.id} value={room.id}>{room.name}{room.roomNumber ? ` - Room ${room.roomNumber}` : ""}</option>)}
          </select>
        </label>
      </div>
      <div className="form-hint">The booking total is recalculated automatically for the new room's tariff. If a balance is due afterwards, use Record payment.</div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!roomId} onClick={() => onSubmit(roomId)}>Move room</Button>
      </div>
    </section>
  );
}

function ExchangeRoomForm({ booking, propertyId, scopeRoomId, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  propertyId: string;
  scopeRoomId?: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (withBookingId: string, withBookingRoomId?: string) => void;
}) {
  const bookings = useQuery({ queryKey: ["bookings", propertyId], queryFn: () => api<Booking[]>(`/bookings?propertyId=${propertyId}`) });
  const [choice, setChoice] = useState("");
  const moving = (booking.allottedRooms ?? []).find((room) => room.id === scopeRoomId);
  const movingRoomId = moving?.roomId ?? booking.room?.id;
  // Every room that is currently holding a stay, on this or another booking, other than the one being swapped.
  type ExchangeOption = { key: string; bookingId: string; bookingRoomId?: string; roomId: string; label: string };
  const options: ExchangeOption[] = (bookings.data ?? [])
    .filter((item) => ["CONFIRMED", "CHECKED_IN"].includes(item.bookingStatus))
    .flatMap((item): ExchangeOption[] => {
      const rooms = item.allottedRooms?.length
        ? item.allottedRooms.filter((room) => room.status === "CONFIRMED" || room.status === "CHECKED_IN").map((room) => ({ key: `${item.id}|${room.id}`, bookingId: item.id, bookingRoomId: room.id, roomId: room.roomId, label: `${item.bookingRef} - ${item.guestName} - ${roomStayLabel(room)}` }))
        : item.room ? [{ key: `${item.id}|`, bookingId: item.id, bookingRoomId: undefined, roomId: item.room.id, label: `${item.bookingRef} - ${item.guestName} - ${item.room.name}` }] : [];
      return rooms;
    })
    .filter((option) => option.roomId !== movingRoomId && option.bookingRoomId !== scopeRoomId);
  const picked = options.find((option) => option.key === choice);

  return (
    <section className="booking-inline-form">
      <h4>Exchange room{moving ? ` - ${roomStayLabel(moving)}` : ""} with another room</h4>
      <div className="property-form-grid">
        <label>Swap with
          <select value={choice} onChange={(event) => setChoice(event.target.value)}>
            <option value="">Select a booking room</option>
            {options.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
          </select>
        </label>
      </div>
      <div className="form-hint">Both bookings' totals are recalculated automatically for their new rooms' tariff. If a balance is due afterwards, use Record payment.</div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!picked} onClick={() => picked && onSubmit(picked.bookingId, picked.bookingRoomId)}>Exchange rooms</Button>
      </div>
    </section>
  );
}

function RatePlanForm({ booking, propertyId, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  propertyId: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { ratePlanId: string | null }) => void;
}) {
  const ratePlans = useQuery({ queryKey: ["rate-plans", propertyId], queryFn: () => api<ListResponse<RatePlan>>(`/rate-plans?propertyId=${propertyId}`) });
  const options = listItems<RatePlan>(ratePlans.data).filter((plan) => plan.isActive);
  const [ratePlanId, setRatePlanId] = useState(booking.ratePlanId ?? "");

  return (
    <section className="booking-inline-form">
      <h4>Change rate plan</h4>
      <div className="property-form-grid">
        <label>Rate plan
          <select value={ratePlanId} onChange={(event) => setRatePlanId(event.target.value)}>
            <option value="">No rate plan</option>
            {options.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
          </select>
        </label>
      </div>
      <div className="form-hint">The booking total is recalculated automatically from the room's tariff and the selected rate plan. Any balance can then be collected with Record payment.</div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} onClick={() => onSubmit({ ratePlanId: ratePlanId || null })}>Save rate plan</Button>
      </div>
    </section>
  );
}

function GuestForm({ initial, loading, error, onCancel, onSubmit }: {
  initial?: BookingGuestMember;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { name: string; phone?: string; email?: string }) => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const phoneDigits = phone.replace(/\D/g, "");
  const phoneError = phone && phoneDigits.length < 7 ? "Enter at least 7 digits" : undefined;

  return (
    <section className="booking-inline-form">
      <h4>{initial ? "Edit guest" : "Add guest"}</h4>
      <div className="property-form-grid">
        <label>Name<input value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label>Phone<PhoneInput value={phone} onChange={setPhone} /></label>
        {phoneError && <small className="field-error">{phoneError}</small>}
        <label className="property-form-grid__wide">Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
      </div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={name.trim().length < 2 || Boolean(phoneError)} onClick={() => onSubmit({ name: name.trim(), phone: phone.trim() || undefined, email: email.trim() || undefined })}>{initial ? "Save guest" : "Add guest"}</Button>
      </div>
    </section>
  );
}

function AddServiceForm({ propertyId, loading, error, onCancel, onSubmit }: {
  propertyId: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { serviceId: string; quantity: number }) => void;
}) {
  const services = useQuery({ queryKey: ["services", propertyId], queryFn: () => api<ListResponse<Service>>(`/services?propertyId=${propertyId}`) });
  const options = listItems<Service>(services.data).filter((service) => service.isAvailable);
  const [serviceId, setServiceId] = useState("");
  const [quantity, setQuantity] = useState("1");

  return (
    <section className="booking-inline-form">
      <h4>Add service</h4>
      <div className="property-form-grid">
        <label>Service
          <select value={serviceId} onChange={(event) => setServiceId(event.target.value)}>
            <option value="">Select a service</option>
            {options.map((service) => <option key={service.id} value={service.id}>{service.title} - {currency.format(Number(service.price))}</option>)}
          </select>
        </label>
        <label>Quantity<input type="number" min={1} value={quantity} onChange={(event) => setQuantity(event.target.value)} /></label>
      </div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!serviceId} onClick={() => onSubmit({ serviceId, quantity: Math.max(1, Number(quantity || 1)) })}>Add service</Button>
      </div>
    </section>
  );
}

function AddChargeForm({ loading, error, onCancel, onSubmit }: {
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { label: string; amount: number }) => void;
}) {
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");

  return (
    <section className="booking-inline-form">
      <h4>Add extra charge</h4>
      <div className="property-form-grid">
        <label>Reason<input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="e.g. Minibar, Damage fee" /></label>
        <label>Amount<input type="number" min={0} step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
      </div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={label.trim().length < 2 || !amount} onClick={() => onSubmit({ label: label.trim(), amount: Number(amount) })}>Add charge</Button>
      </div>
    </section>
  );
}

function BookingHighlight({ icon: Icon, label, value, tone }: { icon: LucideIcon; label: string; value: string; tone: "orange" | "green" | "blue" | "violet" }) {
  return <div className={`booking-highlight booking-highlight--${tone}`}><span><Icon size={18} /></span><small>{label}</small><strong>{value}</strong></div>;
}

function BookingInfoPanel({ title, icon: Icon, rows }: { title: string; icon: LucideIcon; rows: [string, string][] }) {
  return (
    <div className="booking-info-panel">
      <h4><Icon size={17} /> {title}</h4>
      {rows.map(([label, value]) => <div key={label}><small>{label}</small><strong>{value}</strong></div>)}
    </div>
  );
}

function getBookingStatusOptions(status: Booking["bookingStatus"]): Booking["bookingStatus"][] {
  if (status === "PENDING") return ["CONFIRMED", "REJECTED"];
  // Bookings only reach COMPLETED through the check-out flow (see /bookings/:id/check-out),
  // which records the late-checkout charge — there's no direct "Complete" jump from here.
  if (status === "CONFIRMED") return ["CANCELLED"];
  return [];
}

function statusLabel(status: Booking["bookingStatus"]) {
  if (status === "CONFIRMED") return "Confirm";
  if (status === "COMPLETED") return "Complete";
  if (status === "CANCELLED") return "Cancel";
  if (status === "REJECTED") return "Reject";
  return status;
}

function statusIcon(status: Booking["bookingStatus"]) {
  if (status === "CANCELLED" || status === "REJECTED") return <Ban size={15} />;
  return <CheckCircle2 size={15} />;
}

function formatDateTime(value: string, time?: string) {
  const date = new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: PROPERTY_TIME_ZONE }).format(new Date(value));
  return time ? `${date}, ${formatTime(time)}` : `${date}, ${new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit", timeZone: PROPERTY_TIME_ZONE }).format(new Date(value))}`;
}

function formatTime(value: string) {
  const [hours = "0", minutes = "0"] = value.split(":");
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);
  return new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit" }).format(date);
}
